import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import api from '../../utils/api';
import { toast } from 'sonner';
import { User, Plus, Mail, Phone, Briefcase, Star, Edit, Trash2, RefreshCw, Loader2 } from 'lucide-react';

const ContactsTab = ({ clientId }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [syncing, setSyncing] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    job_title: '',
    can_approve_requests: false,
    is_primary: false,
    notes: ''
  });

  useEffect(() => {
    fetchContacts();
  }, [clientId]);

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/clients/${clientId}/contacts`);
      setContacts(res.data);
    } catch (error) {
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name || !formData.email) {
      toast.error('Name and email are required');
      return;
    }

    try {
      if (editingContact) {
        await api.put(`/clients/${clientId}/contacts/${editingContact.id}`, formData);
        toast.success('Contact updated successfully');
      } else {
        await api.post(`/clients/${clientId}/contacts`, formData);
        toast.success('Contact created successfully');
      }
      
      setShowDialog(false);
      setEditingContact(null);
      setFormData({
        name: '',
        email: '',
        phone: '',
        role: '',
        job_title: '',
        can_approve_requests: false,
        is_primary: false,
        notes: ''
      });
      fetchContacts();
    } catch (error) {
      toast.error(editingContact ? 'Failed to update contact' : 'Failed to create contact');
    }
  };

  const handleEdit = (contact) => {
    setEditingContact(contact);
    setFormData({
      name: contact.name,
      email: contact.email,
      phone: contact.phone || '',
      role: contact.role || '',
      job_title: contact.job_title || '',
      can_approve_requests: contact.can_approve_requests || false,
      is_primary: contact.is_primary || false,
      notes: contact.notes || ''
    });
    setShowDialog(true);
  };

  const handleDelete = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) {
      return;
    }

    try {
      await api.delete(`/clients/${clientId}/contacts/${contactId}`);
      toast.success('Contact deleted successfully');
      fetchContacts();
    } catch (error) {
      toast.error('Failed to delete contact');
    }
  };

  const handleSyncFromAssets = async () => {
    setSyncing(true);
    try {
      await api.post(`/clients/${clientId}/sync-contacts-from-assets`);
      toast.success('Contacts synced from assets');
      fetchContacts();
    } catch (error) {
      toast.error('Failed to sync contacts');
    } finally {
      setSyncing(false);
    }
  };

  const getJobTitleBadge = (jobTitle) => {
    const colors = {
      staff: 'bg-gray-100 text-gray-700',
      team_leader: 'bg-blue-100 text-blue-700',
      manager: 'bg-purple-100 text-purple-700',
      director: 'bg-green-100 text-green-700'
    };
    
    if (!jobTitle) return null;
    
    return (
      <Badge className={`${colors[jobTitle] || colors.staff} border-0`}>
        {jobTitle.replace('_', ' ').toUpperCase()}
      </Badge>
    );
  };

  const openNewDialog = () => {
    setEditingContact(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      role: '',
      is_primary: false,
      notes: ''
    });
    setShowDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Contacts ({contacts.length})</h3>
        <div className="flex gap-2">
          <Button
            onClick={handleSyncFromAssets}
            variant="outline"
            className="gap-2"
            disabled={syncing}
          >
            {syncing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            Sync from Assets
          </Button>
          <Button onClick={openNewDialog} className="gap-2">
            <Plus className="w-4 h-4" />
            Add Contact
          </Button>
        </div>
      </div>

      {contacts.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <User className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">No contacts yet</h3>
            <p className="text-gray-500 mb-4">Add contacts manually or sync from your RMM assets</p>
            <div className="flex gap-2 justify-center">
              <Button onClick={openNewDialog} className="gap-2">
                <Plus className="w-4 h-4" />
                Add Contact
              </Button>
              <Button onClick={handleSyncFromAssets} variant="outline" className="gap-2">
                <RefreshCw className="w-4 h-4" />
                Sync from Assets
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {contacts.map((contact) => (
            <Card key={contact.id} className="hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                      <User className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-semibold text-gray-900">{contact.name}</h4>
                        {contact.is_primary && (
                          <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
                            <Star className="w-3 h-3 mr-1" />
                            Primary
                          </Badge>
                        )}
                        {contact.job_title && getJobTitleBadge(contact.job_title)}
                        {contact.can_approve_requests && (
                          <Badge className="bg-green-100 text-green-700 border-0">
                            Can Approve
                          </Badge>
                        )}
                      </div>
                      {contact.role && (
                        <p className="text-sm text-gray-600">{contact.role}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEdit(contact)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(contact.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <a href={`mailto:${contact.email}`} className="text-blue-600 hover:underline">
                      {contact.email}
                    </a>
                  </div>
                  {contact.phone && (
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="w-4 h-4 text-gray-400" />
                      <a href={`tel:${contact.phone}`} className="text-gray-700">
                        {contact.phone}
                      </a>
                    </div>
                  )}
                  {contact.notes && (
                    <div className="mt-3 pt-3 border-t">
                      <p className="text-xs text-gray-600">{contact.notes}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Contact Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingContact ? 'Edit Contact' : 'Add New Contact'}</DialogTitle>
            <DialogDescription>
              {editingContact ? 'Update contact information' : 'Create a new contact for this client'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                placeholder="John Doe"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                placeholder="john@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  placeholder="+1 234 567 8900"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Input
                  id="role"
                  placeholder="Manager, IT Admin, etc."
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="job_title">Job Title / Level</Label>
                <Select
                  value={formData.job_title}
                  onValueChange={(value) => setFormData({ ...formData, job_title: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select job title..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="staff">Staff</SelectItem>
                    <SelectItem value="team_leader">Team Leader</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="director">Director</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">Determines approval permissions for requests</p>
              </div>

              <div className="space-y-2 flex flex-col justify-center">
                <div className="flex items-center gap-2 pt-6">
                  <input
                    type="checkbox"
                    id="can_approve_requests"
                    checked={formData.can_approve_requests}
                    onChange={(e) => setFormData({ ...formData, can_approve_requests: e.target.checked })}
                    className="rounded"
                  />
                  <Label htmlFor="can_approve_requests" className="cursor-pointer">
                    Can approve requests
                  </Label>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_primary"
                checked={formData.is_primary}
                onChange={(e) => setFormData({ ...formData, is_primary: e.target.checked })}
                className="rounded"
              />
              <Label htmlFor="is_primary" className="cursor-pointer">
                Set as primary contact
              </Label>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                placeholder="Additional notes about this contact..."
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave}>
              {editingContact ? 'Update' : 'Create'} Contact
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContactsTab;
